import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { pdApiUrl } from "../../../utils/pdApi";
import { Button } from "semantic-ui-react";
import Table from "../../../components/Table/Table";
import { EditActions, editActions } from "../../../actions/editActions";
import { isEditActiveSelector } from "../../../reducers/editReducer";
import { mainActions, MainActions } from "../../../actions/mainActions";
import styles from "./Publish.module.scss";
import { pdApiActions } from "../../../actions/pdApiActions";
import { error, info } from "react-notification-system-redux";
import axios, { AxiosResponse } from "axios";
import downloadIcon from "../../../images/cloud-download.svg";
import formatDateString, { getNotificationObject, hasWriteAccess } from "../../../utils/utils";
import classNames from "classnames";
import { saveAs } from "file-saver";

type StateProps = {
  columns: State.ColumnMap;
  mainState: State.Main;
  parentObjectId: string;
  isEditActive: boolean;
  mainActions: typeof mainActions;
  isPublishingLocalizations: boolean;
  lastLocalizationPublishCompletedAt: number;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
  editActions: typeof editActions;
  showError: typeof error;
  showInfo: typeof info;
};

type OwnProps = {
  subMenuRoute: string;
  groupName?: Api.GroupName;
  toggleModal: () => void;
};

// Props of the component
export type PublishProps = StateProps & DispatchProps & OwnProps;

type PublishState = {
  publishedFiles: Api.PublishedFilesInLocale[];
  latestFileDate: string;
};

export class Publish extends React.Component<PublishProps, PublishState> {
  state = {
    publishedFiles: [] as Api.PublishedFilesInLocale[],
    latestFileDate: ""
  };

  public async componentDidMount(): Promise<void> {
    await this.fetchPublishedFiles();
  }

  public componentDidUpdate(prevProps: PublishProps): void {
    if (
      prevProps.lastLocalizationPublishCompletedAt !== this.props.lastLocalizationPublishCompletedAt
    ) {
      this.fetchPublishedFiles();
    }
  }

  private fetchPublishedFiles = async (): Promise<void> => {
    // Fetch localizations from PDAPI

    const url = `${pdApiUrl}/localizationFiles`;
    const response: void | AxiosResponse<Promise<void>> = await axios.get(url).catch(e => {
      this.setState(prevState => ({
        ...prevState,
        errorMessage: "Error fetching localizations: " + e,
        fetchingAuxData: false
      }));
      this.props.showError(getNotificationObject("error", "Localization files: " + e));
    });
    const localizationFiles: Api.AnyItem =
      (response as AxiosResponse<Promise<void>>)?.data || undefined;

    const latestFileDate = this.getLatestLocsFileDate(localizationFiles);

    this.setState(prevstate => ({
      ...prevstate,
      publishedFiles: localizationFiles,
      latestFileDate: latestFileDate
    }));
  };

  getLatestLocsFileDate(localizationFiles: Api.PublishedFilesInLocale[]): string {
    if (!localizationFiles || localizationFiles.length === 0) return "(No files published)";
    const newest = Math.max(
      ...localizationFiles
        .map(filesInLocale => filesInLocale.details)
        .map(details => Math.max(...details.map(fileinfo => Date.parse(fileinfo.modified))))
    );
    return formatDateString(new Date(newest).toISOString());
  }

  componentWillUnmount(): void {
    this.props.mainActions.setLastEditedObjectId(undefined, undefined);
  }

  onDownloadClick = async (
    e: { stopPropagation: () => void },
    row: { Edit: string }
  ): Promise<void> => {
    if (this.props.isPublishingLocalizations) {
      return;
    }

    this.props.showInfo(
      getNotificationObject("info", "Downloading localizations for locale: " + row.Edit, {
        id: "DownloadingLocalizationFile",
        autoDismiss: 3
      })
    );

    const url = `${pdApiUrl}/localeZipFile?locale=${row.Edit}`;
    const fileName = `${row.Edit}.zip`;

    axios({
      url: url,
      method: "GET",
      responseType: "blob"
    })
      .then(response => {
        saveAs(response.data, fileName);
      })
      .catch(e => {
        this.setState(prevState => ({
          ...prevState,
          errorMessage: "Error fetching localization: " + e,
          fetchingAuxData: false
        }));
        this.props.showError(getNotificationObject("error", "Localization file: " + e));
      });

    e.stopPropagation();
  };

  SetDownloadButtonHandler = (arg0: State.Column[]): State.Column[] => {
    const downloadColumn = arg0.find(col => col.id === "Edit");
    if (downloadColumn) {
      // eslint-disable-next-line react/display-name, react/prop-types
      downloadColumn.Cell = ({ row }) => (
        <div className={styles.flexRow}>
          <img
            className={classNames(styles.notSelectedColorFilter, {
              // eslint-disable-next-line react/prop-types
              [styles.selectedColorFilter]: row.Locale === this.props.parentObjectId
            })}
            src={downloadIcon}
            alt={"Download"}
          />
          <button onClick={e => this.onDownloadClick(e, row)}>Download</button>
        </div>
      );
    }
    return arg0;
  };

  isParentSelected = (filesInLocale: Api.PublishedFilesInLocale): boolean => {
    const { parentObjectId } = this.props;
    if (
      parentObjectId === filesInLocale.locale ||
      this.props.mainState.lastSelectedParentObjectId === filesInLocale.locale
    ) {
      return true;
    } else {
      return false;
    }
  };

  handleParentSelection = (selectedLocale: Api.PublishedFilesInLocale): void => {
    this.props.editActions.selectParent(selectedLocale);
  };

  private publish = () => {
    if (this.props.isPublishingLocalizations) {
      return;
    }

    this.props.pdApiActions.publishLocalizationFiles();
  };

  render(): JSX.Element {
    const { columns, isEditActive, isPublishingLocalizations } = this.props;
    const rows: Api.PublishedFilesInLocale[] = this.state.publishedFiles
      ? [...this.state.publishedFiles]
      : [];
    const selectedColumns: State.Column[] = this.SetDownloadButtonHandler(
      columns["Published Files Locales"]
    );

    // Split parameters into parent and sub cageories, display parents left
    const parentRows = rows;
    let subRows = null;
    if (this.props.parentObjectId) {
      subRows = this.state.publishedFiles.find(files => files.locale === this.props.parentObjectId)
        ?.details;
    }

    return (
      <div className={styles.publishPage}>
        <div className={styles.containerWithPadding}>
          <h3 className={styles.pageHeader}>Publish translated strings</h3>
          <div className={styles.publishRow}>
            <Button
              id={"publishTextsButton"}
              className={styles.Localizations}
              onClick={this.publish}
              disabled={
                isPublishingLocalizations ||
                !hasWriteAccess(this.props.activeUser, undefined, true)
              }
            >
              {isPublishingLocalizations ? "Publishing..." : "Publish texts"}
            </Button>
          </div>
          {isPublishingLocalizations && (
            <div className={styles.publishRow}>
              <p className={`information ${styles.publishingMessage}`}>
                Publishing localizations. This may take several minutes, you will be notified once it is done.
              </p>
            </div>
          )}
          <div className={styles.publishRow}>
            {!isPublishingLocalizations &&
              !hasWriteAccess(this.props.activeUser, undefined, true) && (
                <p className="color-red">Your role doesn't have neccessary rights</p>
              )}
          </div>
        </div>
        {rows.length > 0 && (
          <>
            <div className={styles.containerWithPadding}>
              <div className={styles.publishRow}>
                <h3 className={styles.pageHeader}>Published files</h3>
                <div id="lastPublished" className={styles.timestamp}>
                  {this.state.latestFileDate}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className={"flex-1 publishTable"}>
                <div className={styles.container}>
                  <Table
                    rows={parentRows}
                    columns={selectedColumns}
                    isHidingTextActive={true}
                    handleRowSelection={
                      isEditActive
                        ? () => {
                            /*  */
                          }
                        : this.handleParentSelection
                    }
                    isRowSelected={this.isParentSelected}
                    pageSize={10000}
                    disablePagination={true}
                    setHeight="fit-content"
                  />
                </div>
                {subRows && (
                  <div className={styles.container}>
                    <Table
                      rows={subRows}
                      columns={columns["Published Files"]}
                      isHidingTextActive={true}
                      handleRowSelection={() => null}
                      isRowSelected={() => false}
                      pageSize={10000}
                      disablePagination={true}
                      setHeight="fit-content"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: State.Root) => ({
  columns: state.columns,
  mainState: state.main,
  isEditActive: isEditActiveSelector(state.editView),
  activeUser: state.main.activeUser,
  isPublishingLocalizations: state.publishingStatus.isPublishingLocalizations,
  lastLocalizationPublishCompletedAt:
    state.publishingStatus.lastLocalizationPublishCompletedAt
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | MainActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch),
  showError: bindActionCreators(error, dispatch),
  showInfo: bindActionCreators(info, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Publish);

